import { useState, useCallback, type Dispatch, type SetStateAction } from 'react';
import { getInvestCurrency } from '#app/helpers/Cache/InvestCurrencyHelper';

interface Currency {
  id: string | number;
  name: string;
  code?: string;
  sign?: string;
  order?: number;
}

interface UseInvestCurrencyAndSearchOptions {
  storageKey: string; // Ключ для хранения валюты (INVEST_CURRENCY)
  searchStorageKey: string; // Ключ для строки поиска в localStorage
  onCurrencyChange?: () => void;
  onSearchChange?: () => void;
}

interface UseInvestCurrencyAndSearchReturn {
  currency: Currency;
  currencyIdForInvalidation: string | number; // ID валюты из своего ключа для инвалидации (равен currency.id)
  setCurrency: (currency: Currency) => void;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  handleSelectCurrency: (item: Currency | null) => void;
  handleSearchChange: (value: string) => void;
}

/**
 * Хук для управления валютой и поиском инвестиций
 * Общая логика для Invests.jsx и ArchiveInvests.jsx
 * Валюта: общий ключ storageKey (INVEST_CURRENCY)
 * Поиск: отдельный ключ searchStorageKey в localStorage
 */
export const useInvestCurrencyAndSearch = (
  options: UseInvestCurrencyAndSearchOptions
): UseInvestCurrencyAndSearchReturn => {
  const { storageKey, searchStorageKey, onCurrencyChange, onSearchChange } = options;

  // Состояние валюты (инициализация из localStorage по своему ключу)
  // Используем унифицированную функцию getInvestCurrency для получения валюты
  const [currency, setCurrency] = useState<Currency>(() => {
    return getInvestCurrency(storageKey);
  });

  const [search, setSearchState] = useState<string>(() => {
    if (typeof window === 'undefined') {
      return '';
    }
    return localStorage.getItem(searchStorageKey) ?? '';
  });

  const setSearch = useCallback(
    (value: SetStateAction<string>) => {
      setSearchState((prev) => {
        const next = typeof value === 'function' ? (value as (p: string) => string)(prev) : value;
        if (typeof window !== 'undefined') {
          localStorage.setItem(searchStorageKey, next);
        }
        return next;
      });
    },
    [searchStorageKey]
  );

  // Обработчик смены валюты
  const handleSelectCurrency = useCallback(
    (item: Currency | null) => {
      if (item) {
        // Обновляем только свой ключ
        const currencyJson = JSON.stringify(item);
        localStorage.setItem(storageKey, currencyJson);
        setCurrency(item);
        // Вызываем опциональный колбэк (например, для сброса страницы в архиве)
        if (onCurrencyChange) {
          onCurrencyChange();
        }
      }
    },
    [onCurrencyChange, storageKey]
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      if (onSearchChange) {
        onSearchChange();
      }
    },
    [onSearchChange, setSearch]
  );

  return {
    currency,
    currencyIdForInvalidation: currency.id, // Всегда равен currency.id, так как оба из одного storageKey
    setCurrency,
    search,
    setSearch,
    handleSelectCurrency,
    handleSearchChange,
  };
};
